
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  BriefcaseBusiness,
  CalendarDays,
  ChevronLeft,
  Clock3,
  Contact,
  FileText,
  GraduationCap,
  KeyRound,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  ShieldCheck,
  UserRound,
} from 'lucide-react';

import { useSchool } from '../../../context/SchoolContext';
import StaffAssignmentsTab from '../components/view/StaffAssignmentsTab';
import StaffDocumentsTab from '../components/view/StaffDocumentsTab';
import Toast from '../components/Toast';
import { staffApi } from '../services/staffApi';
import { getStaffApiErrorMessage } from '../utils/staffErrors';

const showValue = (value) =>
  value !== undefined &&
  value !== null &&
  String(value).trim() !== ''
    ? value
    : '—';

const formatDate = (value, includeTime = false) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(includeTime
      ? { hour: '2-digit', minute: '2-digit' }
      : {}),
  });
};

const InfoRow = ({ label, value, locked = false }) => (
  <div className={`rounded-xl border p-4 ${locked ? 'border-gray-200 bg-gray-50' : 'border-gray-100 bg-white'}`}>
    <div className="flex items-center gap-2">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </p>
      {locked && <KeyRound className="h-3.5 w-3.5 text-gray-400" />}
    </div>
    <p className="mt-2 break-words text-sm font-semibold leading-6 text-gray-900">
      {showValue(value)}
    </p>
  </div>
);

const InfoCard = ({ title, description, icon: Icon, children, className = '' }) => (
  <section className={`rounded-2xl border border-gray-200 bg-white p-6 shadow-sm ${className}`}>
    <div className="mb-5 flex items-start gap-3">
      <div className="rounded-xl bg-blue-50 p-2.5 text-blue-600">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {description && <p className="mt-1 text-xs text-gray-500">{description}</p>}
      </div>
    </div>
    {children}
  </section>
);

const StaffViewPage = () => {
  const { schoolId } = useSchool();
  const navigate = useNavigate();
  const { id } = useParams();

  const [activeTab, setActiveTab] = useState('info');
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const loadStaff = useCallback(async () => {
    if (!schoolId || !id) return;

    setLoading(true);
    try {
      const response = await staffApi.getStaffById(schoolId, id);
      setStaff(response?.data ?? response);
    } catch (error) {
      setToast({
        type: 'error',
        message: getStaffApiErrorMessage(error, 'Unable to load staff details.'),
      });
    } finally {
      setLoading(false);
    }
  }, [schoolId, id]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const viewData = useMemo(() => {
    if (!staff) return null;

    return {
      firstName: staff.first_name || '',
      middleName: staff.middle_name || '',
      lastName: staff.last_name || '',
      fullName:
        staff.full_name ||
        [staff.first_name, staff.middle_name, staff.last_name]
          .filter(Boolean)
          .join(' '),
      employeeId: staff.employee_id || '',
      photo: staff.photo || '',
      staffType: staff.staff_type || '',
      designation: staff.designation || '',
      department: staff.department || '',
      qualification: staff.qualification || '',
      joiningDate: staff.joining_date || '',
      gender: staff.gender || '',
      dob: staff.date_of_birth || '',
      phone: staff.phone || '',
      alternatePhone: staff.alternate_phone || '',
      email: staff.email || '',
      address: staff.address || '',
      status: staff.status || '',
      loginStatus: staff.login_status || '',
      userId: staff.user_id || '',
      userIsActive: staff.user_is_active,
      userIsVerified: staff.user_is_verified,
      createdAt: staff.created_at,
      updatedAt: staff.updated_at,
      classTeacherAssignments: (staff.class_teacher_assignments || []).map((item) => ({
        id: item.id,
        academicYearName: item.academic_year_name,
        className: item.class_name,
        sectionName: item.section_name,
        assignedDate: item.assigned_date,
        notes: item.notes,
        status: item.status,
      })),
      subjectAssignments: (staff.subject_assignments || []).map((item) => ({
        id: item.id,
        academicYearName: item.academic_year_name,
        className: item.class_name,
        sectionName: item.section_name,
        subjectName: item.subject_name,
        status: item.status,
      })),
      documents: (staff.documents || []).map((item) => ({
        id: item.id,
        documentType: item.document_type,
        documentNumber: item.document_number,
        issuedDate: item.issued_date,
        expiryDate: item.expiry_date,
        verificationStatus: item.verification_status,
        createdAt: item.created_at,
        contentType: item.file_record?.content_type || '',
        sizeBytes: item.file_record?.size_bytes || 0,
        fileName:
          item.file_record?.original_filename ||
          item.file_record?.original_name ||
          item.file_record?.file?.split('/').pop() ||
          item.document_type ||
          'Document',
        fileUrl:
          item.file_record?.file_url ||
          item.file_record?.url ||
          item.file_record?.file ||
          '',
      })),
    };
  }, [staff]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <Loader2 className="h-9 w-9 animate-spin text-blue-600" />
        <p className="mt-3 text-sm text-gray-500">Loading staff profile...</p>
      </div>
    );
  }

  if (!viewData) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
        Staff details could not be loaded.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-10">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/staff"
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-blue-600"
        >
          <ChevronLeft className="h-4 w-4" />
          Staff Management
        </Link>

        <button
          type="button"
          onClick={() => navigate(`/staff/${id}/edit`)}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700"
        >
          <Pencil className="h-4 w-4" />
          Edit Staff
        </button>
      </div>

      <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 md:flex-row md:items-center">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-blue-100 text-xl font-bold text-blue-700">
            {viewData.photo ? (
              <img src={viewData.photo} alt={viewData.fullName} className="h-full w-full object-cover" />
            ) : (
              `${viewData.firstName?.[0] || ''}${viewData.lastName?.[0] || ''}`.toUpperCase()
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                {viewData.fullName}
              </h1>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${viewData.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                {viewData.status}
              </span>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${viewData.loginStatus === 'ENABLED' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                Login {viewData.loginStatus}
              </span>
            </div>

            <p className="mt-2 text-sm font-medium text-gray-700">
              {showValue(viewData.designation)} · {showValue(viewData.department)}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Employee ID: {showValue(viewData.employeeId)}
            </p>
          </div>
        </div>
      </section>

      <div className="border-b border-gray-200">
        <div className="flex gap-8 overflow-x-auto">
          {[
            ['info', 'Profile'],
            ['assignments', 'Assignments'],
            ['documents', 'Documents'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`whitespace-nowrap pb-4 text-sm font-semibold ${
                activeTab === key
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'info' && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <InfoCard
            title="Personal Information"
            description="Basic details from the staff API."
            icon={UserRound}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <InfoRow label="First Name" value={viewData.firstName} />
              <InfoRow label="Middle Name" value={viewData.middleName} />
              <InfoRow label="Last Name" value={viewData.lastName} />
              <InfoRow label="Gender" value={viewData.gender} />
              <InfoRow label="Date of Birth" value={formatDate(viewData.dob)} />
              <InfoRow label="Status" value={viewData.status} />
            </div>
          </InfoCard>

          <InfoCard
            title="Employment Information"
            description="Locked system ID and employment information."
            icon={BriefcaseBusiness}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <InfoRow label="Employee ID" value={viewData.employeeId} locked />
              <InfoRow label="Staff Type" value={viewData.staffType} />
              <InfoRow label="Designation" value={viewData.designation} />
              <InfoRow label="Department" value={viewData.department} />
              <InfoRow label="Qualification" value={viewData.qualification} />
              <InfoRow label="Joining Date" value={formatDate(viewData.joiningDate)} />
            </div>
          </InfoCard>

          <InfoCard
            title="Contact Information"
            description="Phone, email, and address details."
            icon={Contact}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <InfoRow label="Phone" value={viewData.phone} />
              <InfoRow label="Alternate Phone" value={viewData.alternatePhone} />
              <InfoRow label="Email" value={viewData.email} locked={Boolean(viewData.userId)} />
              <InfoRow label="Address" value={viewData.address} />
            </div>
          </InfoCard>

          <InfoCard
            title="Login and Account"
            description="Backend-controlled login fields are read-only."
            icon={ShieldCheck}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <InfoRow label="Login Status" value={viewData.loginStatus} locked />
              <InfoRow label="User ID" value={viewData.userId} locked />
              <InfoRow label="User Active" value={viewData.userIsActive ? 'Yes' : 'No'} locked />
              <InfoRow label="User Verified" value={viewData.userIsVerified ? 'Yes' : 'No'} locked />
            </div>
          </InfoCard>

          <InfoCard
            title="System Information"
            description="Audit timestamps supplied by the backend."
            icon={Clock3}
            className="xl:col-span-2"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <InfoRow label="Created At" value={formatDate(viewData.createdAt, true)} locked />
              <InfoRow label="Last Updated" value={formatDate(viewData.updatedAt, true)} locked />
            </div>
          </InfoCard>
        </div>
      )}

      {activeTab === 'assignments' && (
        <StaffAssignmentsTab
          formData={viewData}
          schoolId={schoolId}
          staffId={id}
          loadStaff={loadStaff}
          showToast={showToast}
        />
      )}

      {activeTab === 'documents' && (
        <StaffDocumentsTab
          formData={viewData}
          schoolId={schoolId}
          staffId={id}
          loadStaff={loadStaff}
          showToast={showToast}
        />
      )}
    </div>
  );
};

export default StaffViewPage;
